import { useState, useEffect, useMemo } from 'react';
import { useAuth } from './useAuth';
import {
  getSoonDueMaintenances30,
  getSoonDueMaintenances7,
  getOverdueMaintenances,
  getOverdueAuditAssets,
  getSoonAuditAssets7,
  getSoonAuditAssets30,
  getAssetSummary,
  getCompanyStats,
  getCompanyAssetStats,
  getCompanyDisposedStats,
  getAssetStatusByCompany
} from '../services/api';
import {
  DashboardData,
  MaintenanceSchedule,
  AssetAudit,
  AssetSummary
} from '../constants/dashboard';

export const useDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData>({
    overdueMaintenances: [],
    soonMaintenances7: [],
    soonMaintenances30: [],
    overdueAudits: [],
    soonAudits7: [],
    soonAudits30: [],
    summary: { total_assets: 0, total_value: 0, disposed_count: 0, in_use_count: 0 },
    companyStats: [],
    companyAssetStats: [],
    companyDisposedStats: [],
    assetStatusByCompany: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lọc dữ liệu theo quyền
  const filterDataByRole = (data: any[]): any[] => {
    if (!user) return [];
    if (user.role === 'super_admin') return data;
    if (user.role === 'company_admin' || user.role === 'manager') {
      return data.filter((item: any) => item.company_id === user.company_id);
    }
    if (user.role === 'employee_user') return data; // Backend already filtered this
    return [];
  };

  // Load dashboard data
  const loadDashboardData = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const isEmployee = user.role === 'employee_user';

      const [
        overdueM, soonM7, soonM30, overdueA, soonA7, soonA30,
        summaryData, companyStatsData, companyAssetStatsData,
        companyDisposedStatsData, assetStatusByCompanyData
      ] = await Promise.all([
        getOverdueMaintenances(),
        getSoonDueMaintenances7(),
        getSoonDueMaintenances30(),
        getOverdueAuditAssets(),
        getSoonAuditAssets7(),
        getSoonAuditAssets30(),
        getAssetSummary(),
        isEmployee ? Promise.resolve([]) : getCompanyStats(),
        isEmployee ? Promise.resolve([]) : getCompanyAssetStats(),
        isEmployee ? Promise.resolve([]) : getCompanyDisposedStats(),
        isEmployee ? Promise.resolve([]) : getAssetStatusByCompany()
      ]);

      setData({
        overdueMaintenances: overdueM as MaintenanceSchedule[],
        soonMaintenances7: soonM7 as MaintenanceSchedule[],
        soonMaintenances30: soonM30 as MaintenanceSchedule[],
        overdueAudits: overdueA as AssetAudit[],
        soonAudits7: soonA7 as AssetAudit[],
        soonAudits30: soonA30 as AssetAudit[],
        summary: summaryData as AssetSummary,
        companyStats: companyStatsData as any[],
        companyAssetStats: companyAssetStatsData as any[],
        companyDisposedStats: companyDisposedStatsData as any[],
        assetStatusByCompany: assetStatusByCompanyData as any[]
      });
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError('Không thể load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // Computed values
  const filteredData = useMemo(() => ({
    overdueMaintenances: filterDataByRole(data.overdueMaintenances),
    soonMaintenances7: filterDataByRole(data.soonMaintenances7),
    soonMaintenances30: filterDataByRole(data.soonMaintenances30),
    overdueAudits: filterDataByRole(data.overdueAudits),
    soonAudits7: filterDataByRole(data.soonAudits7),
    soonAudits30: filterDataByRole(data.soonAudits30)
  }), [data, user]);

  // Loại bỏ các lịch đã nằm trong 7 ngày khỏi 30 ngày để tránh trùng lặp
  const soonMaintenances30Only = useMemo(() =>
    filteredData.soonMaintenances30.filter(
      (item: MaintenanceSchedule) => !filteredData.soonMaintenances7.some((i: MaintenanceSchedule) => i.id === item.id)
    ), [filteredData.soonMaintenances30, filteredData.soonMaintenances7]
  );

  const soonAudits30Only = useMemo(() =>
    filteredData.soonAudits30.filter(
      (item: AssetAudit) => !filteredData.soonAudits7.some((i: AssetAudit) => i.id === item.id)
    ), [filteredData.soonAudits30, filteredData.soonAudits7]
  );

  // Load data when user changes
  useEffect(() => {
    loadDashboardData();
  }, [user]);

  return {
    data,
    filteredData,
    soonMaintenances30Only,
    soonAudits30Only,
    loading,
    error,
    filterDataByRole,
    reload: loadDashboardData
  };
}; 